<template>
  <Modal>
    <Modal.Title>Remove {{ type }}?</Modal.Title>
    <Modal.Body>
      <p>
        Please confirm you would like to remove this {{ type }}. Any files you
        have added with this owner will not be affected.
      </p>
      <Alert
        v-if="error?.message"
        class="mt-4"
        variant="error"
        :icon="AlertIcon"
      >
        {{ error.message }}
      </Alert>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="stroke" :disabled="loading" @click="close()">
        Cancel
      </Button>
      <Button
        variant="danger"
        :loading="loading"
        @click="handleSave"
      >
        Yes, remove
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import gql from 'graphql-tag'
import { Modal, Button, Alert, useModal } from '@dacr/library'
import { AlertIcon } from '@dacr/icons'
import {
  CopyrightOwnerType,
  CopyrightOwnersDocument,
  CopyrightOwnersNewDocument,
  useDeleteCopyrightOwnerMutation,
  type CopyrightOwnerNew,
  type CopyrightOwnersQuery,
  type CopyrightOwnersNewQuery,
  type User,
} from '@/shared/graphql/legacy/generated'
import { useUserStore } from '@/shared/stores/user'

const { close } = useModal()

const props = defineProps<{
  owner: CopyrightOwnerNew
}>()

const {
  loading,
  mutate: deleteCopyrightOwner,
  error,
} = useDeleteCopyrightOwnerMutation()

const userStore = useUserStore()

const type = computed(() =>
  props.owner.type === CopyrightOwnerType.Organization
    ? 'organization'
    : 'copyright owner'
)

const handleSave = async () => {
  await deleteCopyrightOwner(
    { id: props.owner.id },
    {
      update(cache, { data }) {
        if (!data) return

        cache.updateQuery<CopyrightOwnersNewQuery>(
          { query: CopyrightOwnersNewDocument },
          (response) => {
            if (!response) return

            return {
              ...response,
              copyrightOwnersNew: {
                ...response.copyrightOwnersNew,
                items: response.copyrightOwnersNew.items.filter(
                  (item) => item.id !== props.owner.id
                ),
                total: Math.max(response.copyrightOwnersNew.total - 1, 0),
              },
            }
          }
        )

        cache.updateQuery<CopyrightOwnersQuery>(
          { query: CopyrightOwnersDocument },
          (response) => {
            if (!response) return

            return {
              ...response,
              copyrightOwners: {
                ...response.copyrightOwners,
                items: response.copyrightOwners.items.filter(
                  (item) => item.id !== props.owner.id
                ),
              },
            }
          }
        )

        cache.updateFragment<User>(
          {
            id: cache.identify({
              __typename: 'User',
              id: userStore.user?.id,
            }),
            fragment: gql`
              fragment DecreaseCopyrightOwnersTotal on User {
                totalCopyrightOwners
              }
            `,
          },
          (response) => {
            if (!response) return

            return {
              ...response,
              totalCopyrightOwners: Math.max(
                (response.totalCopyrightOwners || 0) - 1,
                0
              ),
            }
          }
        )
      },
    }
  )

  close()
}
</script>
